/**
 * VehicleIntakeQrCard (Jun-2026, User-Wunsch) — sichtbarer QR-Code für den
 * Fahrzeug-Eingang direkt im Disposition-Header (rechts), ohne Button/Modal.
 *
 * Der QR zeigt auf die standortgebundene Intake-Scan-Session (`portal_url`).
 * Mit dem Handy gescannt öffnet er die mobile Fahrzeug-Eingang-Erfassung.
 * Sobald ein Fahrzeug erkannt wurde, werden die 3 QR-Etiketten automatisch
 * gedruckt (verstecktes Off-Screen-iframe → kein Popup-Blocker). Klick auf die
 * Karte vergrößert den QR und bietet Druck/Status.
 */
import { useEffect, useRef, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Smartphone, Printer, X, MapPin, CheckCircle2, KeyRound, History, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { openQrPrint, dealerPrintInfo } from "../../lib/qrPrint";
import { QrLabelSheetButton } from "../QrLabelSheetButton";

export default function VehicleIntakeQrCard({ orgBranch }) {
    const { user } = useAuth();
    const [session, setSession] = useState(null); // {token, portal_url, branch_label}
    const [result, setResult] = useState(null);
    const [open, setOpen] = useState(false);
    const [printSrc, setPrintSrc] = useState("");
    const pollRef = useRef(null);

    // Session bei Standortwechsel neu erzeugen (key über orgBranch im Parent).
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.post("/work-orders/scan-sessions", { purpose: "intake" });
                if (!cancelled) setSession(data);
            } catch { /* still */ }
        })();
        return () => { cancelled = true; };
    }, []);

    useEffect(() => {
        if (!session?.token || result) return;
        pollRef.current = setInterval(async () => {
            try {
                const { data } = await api.get(`/work-orders/scan-sessions/${session.token}`);
                if (data.status === "complete" && data.intake) {
                    setResult(data.intake);
                    clearInterval(pollRef.current);
                }
            } catch { /* weiter pollen */ }
        }, 2500);
        return () => clearInterval(pollRef.current);
    }, [session, result]);

    // Auto-Druck der 3 QR-Etiketten, sobald ein Fahrzeug erkannt wurde.
    useEffect(() => {
        if (result?.found && result?.listing_id) {
            setPrintSrc(`/listing/${result.listing_id}/qrcard?autoprint=1`);
            const name = [result.vehicle?.brand, result.vehicle?.model].filter(Boolean).join(" ") || "Fahrzeug";
            toast.success(`Eingang erfasst: ${name} · Schlüssel ${result.key_tag || "—"}`);
            setOpen(true);
        } else if (result && !result.found) {
            toast.message("Kein Treffer im Bestand — bitte manuell prüfen.");
        }
    }, [result]);

    const scanUrl = session?.portal_url
        ? (session.portal_url.startsWith("http") ? session.portal_url : `${window.location.origin}${session.portal_url}`)
        : "";

    const handlePrint = () => openQrPrint({
        title: "Fahrzeug-Eingang per Handy starten",
        subtitle: "Mit der Handy-Kamera scannen — öffnet die Fahrzeug-Eingang-Erfassung (FIN scannen oder eintippen).",
        scanUrl,
        ...dealerPrintInfo(user),
    });

    return (
        <div className="flex flex-col items-center gap-1.5" data-testid="vehicle-intake-qr-card">
            <div className="relative">
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    title="Mit dem Handy scannen → Fahrzeug-Eingang erfassen (Klick: vergrößern & drucken)"
                    className="bg-white rounded-md p-2.5 hover:ring-2 hover:ring-[#FFCC00] transition-shadow shadow-lg"
                    data-testid="vehicle-intake-qr-trigger"
                >
                    {scanUrl ? (
                        <QRCodeSVG value={scanUrl} size={92} level="M" data-testid="vehicle-intake-qr-code" role="img" aria-label="QR-Code: Fahrzeug-Aufnahme starten" />
                    ) : (
                        <div className="w-[92px] h-[92px] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>
                    )}
                </button>
                {open && (
                    <div className="absolute right-0 top-[120px] z-50 w-72 bg-white border border-gray-200 rounded-sm shadow-xl p-4 text-[#111827]" data-testid="vehicle-intake-qr-panel">
                        <div className="flex items-center justify-between mb-2">
                            <span className="text-xs font-bold uppercase tracking-wider text-gray-700">Fahrzeug-Eingang per Handy</span>
                            <button type="button" onClick={() => setOpen(false)} data-testid="vehicle-intake-qr-close"><X className="w-4 h-4 text-gray-500" /></button>
                        </div>
                        {result?.found ? (
                            <div className="text-center py-1" data-testid="vehicle-intake-result">
                                <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-1" />
                                <div className="font-bold">{[result.vehicle?.brand, result.vehicle?.model].filter(Boolean).join(" ") || "Fahrzeug erkannt"}</div>
                                <div className="text-xs text-gray-500">{result.vehicle?.vin}</div>
                                <div className="mt-2 inline-flex items-center gap-2 bg-[#FFCC00] text-[#111827] font-black rounded-sm px-3 py-1.5" data-testid="vehicle-intake-keytag">
                                    <KeyRound className="w-4 h-4" /> Schlüssel: {result.key_tag || "—"}
                                </div>
                                <div className="mt-3 text-left bg-gray-50 border border-gray-200 rounded-sm divide-y divide-gray-200" data-testid="vehicle-intake-locations">
                                    {result.origin?.label && (
                                        <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid="vehicle-intake-origin">
                                            <History className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                            <span className="text-gray-500 w-20 shrink-0">Herkunft</span>
                                            <b className="text-gray-800 truncate">{result.origin.label}</b>
                                        </div>
                                    )}
                                    {result.previous_location?.label && (
                                        <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid="vehicle-intake-previous">
                                            <MapPin className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                                            <span className="text-gray-500 w-20 shrink-0">Vorher</span>
                                            <span className="text-gray-700 truncate">{result.previous_location.label}</span>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 px-2.5 py-1.5 text-xs" data-testid="vehicle-intake-now">
                                        <MapPin className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                                        <span className="text-gray-500 w-20 shrink-0">Jetzt hier</span>
                                        <b className="text-emerald-700 truncate">{result.branch_label}</b>
                                    </div>
                                </div>
                                {result.duplicate_warning && (
                                    <div className="mt-2.5 text-[11px] text-red-800 bg-red-50 border border-red-300 rounded-sm px-2.5 py-2 flex items-start gap-1.5 text-left" data-testid="vehicle-intake-duplicate">
                                        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0 text-red-500" />
                                        <span><b>Doppelannahme:</b> war zuvor an {result.duplicate_warning.branch_label || "anderem Standort"} — jetzt umgebucht.</span>
                                    </div>
                                )}
                                <button type="button" onClick={() => result.listing_id && window.open(`/listing/${result.listing_id}/qrcard?autoprint=1`, "_blank")}
                                    className="mt-3 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                                    data-testid="vehicle-intake-reprint">
                                    <Printer className="w-4 h-4" /> Etiketten erneut drucken
                                </button>
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-center bg-white p-2 rounded-sm border-2 border-[#FFCC00]">
                                    {scanUrl ? <QRCodeSVG value={scanUrl} size={180} level="M" role="img" aria-label="QR-Code: Fahrzeug-Aufnahme starten" /> : <Loader2 className="w-7 h-7 animate-spin text-gray-400" />}
                                </div>
                                {session?.branch_label && (
                                    <div className="mt-2 text-[11px] text-gray-600 inline-flex items-center gap-1.5">
                                        <MapPin className="w-3 h-3 text-emerald-600" /> Eingang für: <b>{session.branch_label}</b>
                                    </div>
                                )}
                                <p className="text-[11px] text-gray-600 mt-1">Mit dem Handy scannen: öffnet die Fahrzeug-Eingang-Erfassung (FIN scannen oder eintippen).</p>
                                <div className="text-[11px] text-gray-400 mt-1 inline-flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Warte auf Scan vom Handy…</div>
                                <button type="button" onClick={handlePrint}
                                    className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-[#111827] text-white rounded-sm py-2 text-xs font-bold uppercase tracking-wider hover:bg-black"
                                    data-testid="vehicle-intake-qr-print">
                                    <Printer className="w-4 h-4" /> QR-Code drucken
                                </button>
                                <QrLabelSheetButton scanUrl={scanUrl}
                                    label="Fahrzeug-Eingang per Handy — QR scannen"
                                    testidPrefix="vehicle-intake-qr-label" />
                            </>
                        )}
                    </div>
                )}
            </div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-gray-300 flex items-center gap-1">
                <Smartphone className="w-3 h-3" /> Fahrzeug-Eingang
            </span>
            {printSrc && (
                <iframe title="qr-autoprint" src={printSrc} data-testid="vehicle-intake-print-frame"
                    style={{ position: "fixed", width: 0, height: 0, border: 0, left: -9999, top: -9999 }} />
            )}
        </div>
    );
}
